import { Injectable } from '@angular/core';
import {
  AddToCartItem,
  Cart,
  Collection,
  Product,
  ProductVariant,
} from '../classes/utility';
import { FbpService } from './fbp.service';
import { GtmService } from './gtm.service';
import { JsonLdService } from './jsonld.service';
import { KlaviyoService } from './klaviyo.service';
import { ShopifyAnalyticsService } from './shopify-analytics-helpers/shopify-analytics.service';
import { ThemeService } from './theme.service';

declare global {
  interface Window {
    TriplePixel: any;
  }
}
type Action =
  | 'viewPage'
  | 'viewContentPage'
  | 'viewProduct'
  | 'addToCart'
  | 'viewCollection'
  | 'viewCart'
  | 'startCheckout'
  | 'addToWishlist';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  constructor(
    private themeService: ThemeService,
    private gtmService: GtmService,
    private fbpService: FbpService,
    private klaviyoService: KlaviyoService,
    private shopifyAnalyticsService: ShopifyAnalyticsService,
    private jsonLDService: JsonLdService
  ) {
    this.themeService.themeEvents.subscribe((data) => {
      if (data.action && this.isActionValid(data.action)) {
        try {
          (this[data.action] as Function).call(this, data.payload);
        } catch (err) {
          console.log(err);
        }
      }
    });
  }

  private isActionValid(action: string): action is Action {
    return [
      'viewPage',
      'viewContentPage',
      'viewProduct',
      'addToCart',
      'viewCollection',
      'viewCart',
      'startCheckout',
      'addToWishlist',
    ].includes(action);
  }

  init() {}

  viewPage(payload: { page: any }) {
    const { page } = payload;
    // this.klaviyoService.viewPage(page);
    // this.gtmService.viewPage(page);
    this.fbpService.viewPage(page);
    this.shopifyAnalyticsService.viewPage(page);
  }

  // Home, content and blog index pages: Shopify page view only. Deliberately not
  // Meta ViewContent (fired by viewPage), which is a product-interest signal.
  viewContentPage(payload: { page: any }) {
    this.shopifyAnalyticsService.viewPage(payload.page);
  }

  viewProduct(payload: { product: Product }) {
    const { product } = payload;
    this.klaviyoService.viewProduct(product);
    this.gtmService.viewProduct(product);
    this.fbpService.viewProduct(product);
    this.shopifyAnalyticsService.viewPage(product);
    this.jsonLDService.generateProductJsonLd(product);
  }

  viewCollection(payload: { collection: Collection }) {
    const { collection } = payload;
    // this.klaviyoService.viewCollection(collection);
    this.gtmService.viewCollection(collection);
    this.fbpService.viewCollection(collection);
    this.shopifyAnalyticsService.viewPage(collection);
  }

  addToCart(payload: { addToCartItems: AddToCartItem[]; cart: Cart }) {
    const { addToCartItems, cart } = payload;
    this.klaviyoService.addToCart(addToCartItems, cart);
    this.gtmService.addToCart(addToCartItems, cart);
    this.fbpService.addToCart(addToCartItems, cart);
    this.shopifyAnalyticsService.addToCart(addToCartItems, cart);
  }

  viewCart(payload: { cart: Cart }) {
    const { cart } = payload;
    // this.klaviyoService.viewCart(cart);
    this.gtmService.viewCart(cart);
    this.shopifyAnalyticsService.viewPage(cart);
    this.fbpService.viewCart(cart);
  }

  startCheckout(payload: { cart: Cart }) {
    const { cart } = payload;
    // this.klaviyoService.startCheckout(cart);
    // this.gtmService.startCheckout(cart);
    this.fbpService.startCheckout(cart);
  }

  addToWishlist(payload: { variant: ProductVariant }) {
    const { variant } = payload;
    // this.klaviyoService.addToWishlist(variant);
    // this.gtmService.addToWishlist(variant);
    this.fbpService.addToWishlist(variant);
  }

  search(payload: { query: string }) {
    const { query } = payload;
    // this.klaviyoService.search(query);
    // this.gtmService.search(query);
    this.fbpService.search(query);
    this.shopifyAnalyticsService.viewPage(query);
  }
}
